(function () {
  'use strict';

  class Sa2026CompetitorComparison extends HTMLElement {
    constructor() {
      super();
      this.handleClick = this.handleClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
      this.handleTabsScroll = this.handleTabsScroll.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.handleMutations = this.handleMutations.bind(this);
    }

    connectedCallback() {
      if (this.isConnectedToEvents) return;

      this.refreshItems();
      this.addEventListener('click', this.handleClick);
      this.addEventListener('keydown', this.handleKeydown);
      this.tabsViewport?.addEventListener('scroll', this.handleTabsScroll, { passive: true });
      window.addEventListener('resize', this.handleResize);
      document.addEventListener('shopify:block:select', this.handleBlockSelect);
      this.tabsObserver = new MutationObserver(this.handleMutations);
      this.tabsObserver.observe(this, { childList: true, characterData: true, subtree: true });
      this.updateTabControls();
      this.measureFrame = requestAnimationFrame(() => {
        this.refreshItems();
        this.updateTabControls();
      });
      this.isConnectedToEvents = true;
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      this.removeEventListener('keydown', this.handleKeydown);
      this.tabsViewport?.removeEventListener('scroll', this.handleTabsScroll);
      window.removeEventListener('resize', this.handleResize);
      document.removeEventListener('shopify:block:select', this.handleBlockSelect);
      this.tabsObserver?.disconnect();
      if (this.measureFrame) cancelAnimationFrame(this.measureFrame);
      this.tabsObserver = null;
      this.measureFrame = null;
      this.isConnectedToEvents = false;
    }

    refreshItems() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
      this.tabsViewport = this.querySelector('.sa-2026-competitor-comparison__tabs-viewport');
      this.previousControl = this.querySelector('[data-previous-tab]');
      this.nextControl = this.querySelector('[data-next-tab]');
    }

    handleTabsScroll() {
      this.updateTabControls();
    }

    handleResize() {
      this.refreshItems();
      this.updateTabControls();
    }

    handleMutations() {
      this.refreshItems();
      this.updateTabControls();
    }

    handleClick(event) {
      if (!(event.target instanceof Element)) return;

      const nextControl = event.target.closest('[data-next-tab]');
      if (nextControl && this.contains(nextControl)) {
        this.refreshItems();
        const activeIndex = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
        const nextIndex = activeIndex < 0 ? 0 : (activeIndex + 1) % this.tabs.length;
        this.activate(nextIndex);
        return;
      }

      const previousControl = event.target.closest('[data-previous-tab]');
      if (previousControl && this.contains(previousControl)) {
        this.refreshItems();
        const activeIndex = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
        const previousIndex = activeIndex <= 0 ? this.tabs.length - 1 : activeIndex - 1;
        this.activate(previousIndex);
        return;
      }

      const tab = event.target.closest('[role="tab"]');
      if (!tab || !this.contains(tab)) return;

      this.refreshItems();
      this.activate(this.tabs.indexOf(tab));
    }

    handleKeydown(event) {
      if (!(event.target instanceof Element)) return;

      const tab = event.target.closest('[role="tab"]');
      if (!tab || !this.contains(tab)) return;

      this.refreshItems();
      const index = this.tabs.indexOf(tab);
      let nextIndex;

      if (event.key === 'ArrowRight') nextIndex = (index + 1) % this.tabs.length;
      if (event.key === 'ArrowLeft') nextIndex = (index - 1 + this.tabs.length) % this.tabs.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = this.tabs.length - 1;
      if (nextIndex === undefined) return;

      event.preventDefault();
      this.activate(nextIndex, true);
    }

    handleBlockSelect(event) {
      if (!(event.target instanceof Element)) return;

      const panel = event.target.closest('[role="tabpanel"]');
      if (!panel || panel.closest('sa-2026-competitor-comparison') !== this) return;

      this.refreshItems();
      this.activate(this.panels.indexOf(panel));
    }

    activate(index, moveFocus = false) {
      if (index < 0 || !this.tabs[index] || !this.panels[index]) return;

      this.tabs.forEach((tab, tabIndex) => {
        const isActive = tabIndex === index;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', String(isActive));
        tab.tabIndex = isActive ? 0 : -1;
      });

      this.panels.forEach((panel, panelIndex) => {
        const isActive = panelIndex === index;
        panel.classList.toggle('is-active', isActive);
        panel.hidden = !isActive;
      });

      this.scrollTabIntoView(this.tabs[index]);
      this.updateTabControls();
      if (moveFocus) this.tabs[index].focus();
    }

    updateTabControls() {
      if (!this.tabsViewport) return;

      const overflowTolerance = 1;
      const maxScrollLeft = this.tabsViewport.scrollWidth - this.tabsViewport.clientWidth;
      const hasOverflow = maxScrollLeft > overflowTolerance;
      const scrollLeft = Math.max(0, this.tabsViewport.scrollLeft);

      if (this.nextControl) {
        this.nextControl.hidden = !hasOverflow || scrollLeft >= maxScrollLeft - overflowTolerance;
      }
      if (this.previousControl) {
        this.previousControl.hidden = !hasOverflow || scrollLeft <= overflowTolerance;
      }
    }

    scrollTabIntoView(tab) {
      const viewport = this.querySelector('.sa-2026-competitor-comparison__tabs-viewport');
      if (!viewport || viewport.scrollWidth <= viewport.clientWidth) return;

      const targetLeft = tab.offsetLeft - (viewport.clientWidth - tab.offsetWidth) / 2;
      viewport.scrollTo({ left: Math.max(0, targetLeft), behavior: 'smooth' });
    }
  }

  if (!customElements.get('sa-2026-competitor-comparison')) {
    customElements.define('sa-2026-competitor-comparison', Sa2026CompetitorComparison);
  }
})();
